/**
 * Phase 5.1: Redux Workspace State Persistence
 *
 * Ensures pillar selections, scenario choices, and tab state persist across navigation.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../store'

interface WorkspaceTabState {
  activeSubTab: string
  scrollPosition: number
  expandedSections: string[]
}

interface ConversationWorkspaceState {
  selectedPillars: string[]
  activeScenarioId: string | null
  tabStates: Record<string, WorkspaceTabState>
  highlightedNodeId: string | null
  isSelectionMode: boolean
  isReorderMode: boolean
  lastUpdated: string
}

interface WorkspaceState {
  // Per-conversation workspace state
  workspaces: Record<string, ConversationWorkspaceState>
}

const initialState: WorkspaceState = {
  workspaces: {}
}

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    // Initialize workspace for a conversation
    initWorkspace: (
      state,
      action: PayloadAction<{ conversationId: string; pillars: string[] }>
    ) => {
      const { conversationId, pillars } = action.payload
      if (!state.workspaces[conversationId]) {
        state.workspaces[conversationId] = {
          selectedPillars: pillars,  // Default: all selected
          activeScenarioId: null,
          tabStates: {},
          highlightedNodeId: null,
          isSelectionMode: false,
          isReorderMode: false,
          lastUpdated: new Date().toISOString()
        }
      }
    },

    // Set selected pillars
    setSelectedPillars: (
      state,
      action: PayloadAction<{ conversationId: string; pillars: string[] }>
    ) => {
      const { conversationId, pillars } = action.payload
      if (state.workspaces[conversationId]) {
        state.workspaces[conversationId].selectedPillars = pillars
        state.workspaces[conversationId].lastUpdated = new Date().toISOString()
      }
    },

    // Toggle a single pillar selection
    togglePillarSelection: (
      state,
      action: PayloadAction<{ conversationId: string; pillarName: string }>
    ) => {
      const { conversationId, pillarName } = action.payload
      const workspace = state.workspaces[conversationId]
      if (workspace) {
        const idx = workspace.selectedPillars.indexOf(pillarName)
        if (idx >= 0) {
          // Only allow removing if not the last one
          if (workspace.selectedPillars.length > 1) {
            workspace.selectedPillars.splice(idx, 1)
          }
        } else {
          workspace.selectedPillars.push(pillarName)
        }
        workspace.lastUpdated = new Date().toISOString()
      }
    },

    // Set tab state for a specific tab
    setTabState: (
      state,
      action: PayloadAction<{
        conversationId: string
        tabId: string
        tabState: Partial<WorkspaceTabState>
      }>
    ) => {
      const { conversationId, tabId, tabState } = action.payload
      const workspace = state.workspaces[conversationId]
      if (workspace) {
        const defaultTabState: WorkspaceTabState = {
          activeSubTab: '',
          scrollPosition: 0,
          expandedSections: []
        }
        workspace.tabStates[tabId] = {
          ...defaultTabState,
          ...workspace.tabStates[tabId],
          ...tabState
        }
        workspace.lastUpdated = new Date().toISOString()
      }
    },

    // Set highlighted node for cross-tab navigation
    setHighlightedNode: (
      state,
      action: PayloadAction<{ conversationId: string; nodeId: string | null }>
    ) => {
      const { conversationId, nodeId } = action.payload
      if (state.workspaces[conversationId]) {
        state.workspaces[conversationId].highlightedNodeId = nodeId
        state.workspaces[conversationId].lastUpdated = new Date().toISOString()
      }
    },

    // Set selection mode
    setSelectionMode: (
      state,
      action: PayloadAction<{ conversationId: string; enabled: boolean }>
    ) => {
      const { conversationId, enabled } = action.payload
      if (state.workspaces[conversationId]) {
        state.workspaces[conversationId].isSelectionMode = enabled
        state.workspaces[conversationId].lastUpdated = new Date().toISOString()
      }
    },

    // Set reorder mode
    setReorderMode: (
      state,
      action: PayloadAction<{ conversationId: string; enabled: boolean }>
    ) => {
      const { conversationId, enabled } = action.payload
      if (state.workspaces[conversationId]) {
        state.workspaces[conversationId].isReorderMode = enabled
        state.workspaces[conversationId].lastUpdated = new Date().toISOString()
      }
    },

    // Set active scenario ID
    setActiveScenarioId: (
      state,
      action: PayloadAction<{ conversationId: string; scenarioId: string | null }>
    ) => {
      const { conversationId, scenarioId } = action.payload
      if (state.workspaces[conversationId]) {
        state.workspaces[conversationId].activeScenarioId = scenarioId
        state.workspaces[conversationId].lastUpdated = new Date().toISOString()
      }
    },

    // Clear workspace for a conversation
    clearWorkspace: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload
      delete state.workspaces[conversationId]
    },

    // Update workspace from external data (e.g., from backend)
    syncWorkspace: (
      state,
      action: PayloadAction<{
        conversationId: string
        data: Partial<ConversationWorkspaceState>
      }>
    ) => {
      const { conversationId, data } = action.payload
      if (state.workspaces[conversationId]) {
        state.workspaces[conversationId] = {
          ...state.workspaces[conversationId],
          ...data,
          lastUpdated: new Date().toISOString()
        }
      }
    }
  }
})

// Export actions
export const {
  initWorkspace,
  setSelectedPillars,
  togglePillarSelection,
  setTabState,
  setHighlightedNode,
  setSelectionMode,
  setReorderMode,
  setActiveScenarioId,
  clearWorkspace,
  syncWorkspace
} = workspaceSlice.actions

// Selectors
export const selectWorkspace = (state: RootState, conversationId: string) =>
  state.workspace?.workspaces[conversationId] || null

export const selectSelectedPillars = (state: RootState, conversationId: string) =>
  state.workspace?.workspaces[conversationId]?.selectedPillars || []

export const selectTabState = (state: RootState, conversationId: string, tabId: string) =>
  state.workspace?.workspaces[conversationId]?.tabStates[tabId] || null

export const selectHighlightedNode = (state: RootState, conversationId: string) =>
  state.workspace?.workspaces[conversationId]?.highlightedNodeId || null

export const selectIsSelectionMode = (state: RootState, conversationId: string) =>
  state.workspace?.workspaces[conversationId]?.isSelectionMode || false

export const selectIsReorderMode = (state: RootState, conversationId: string) =>
  state.workspace?.workspaces[conversationId]?.isReorderMode || false

export default workspaceSlice.reducer
